import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import Global from '../../../store/global-selectors';
import { createLabelSections, standardSections, externalFulfillmentSections } from '../../../services/other-shipping-options/constants';
import { EXTERNAL } from '../../../constants/fulfillment-types';
import { useShouldShowDdp } from '../use-should-show-ddp';
import { RATE_BROWSER, CUSTOMERS_CREATE_LABEL } from '../../../enums/csw-types';
import ReadOnlyOtherShippingOptions from './read-only-component';
import { THIRD_PARTY } from '../../../enums/bill-to-party';

const messages = defineMessages({
  thirdParty: {
    description: 'otherShippingOptions.thirdParty',
    defaultMessage: 'Third Party',
    id: 'aY/GVH',
  },
  recipient: {
    description: 'otherShippingOptions.recipient',
    defaultMessage: 'Recipient',
    id: '+xjCUw',
  },
});

const getShippingAccountDisplayName = (billToParty) => {
  if (billToParty === THIRD_PARTY) {
    return <FormattedMessage {...messages.thirdParty} />;
  }
  return <FormattedMessage {...messages.recipient} />;
};

const getSectionsToShow = (fulfillmentPlanId, fulfillmentType) => {
  if (fulfillmentType === EXTERNAL) {
    return externalFulfillmentSections;
  }
  const isEmptyShipment = [RATE_BROWSER, CUSTOMERS_CREATE_LABEL].includes(fulfillmentPlanId);
  return isEmptyShipment ? createLabelSections : standardSections;
};

export const ReadOnlyOtherShippingOptionsWrapper = React.memo(({ fulfillmentPlanId, optionData }) => {
  const sellerServices = useSelector(Global.selectSellerServices);
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const { fulfillmentType, options, serviceId, sellerLabelProviderId } =
    useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) || {};
  const shouldShowDdp = useShouldShowDdp(fulfillmentPlanId);

  const selectedSellerLabelProviderId = sellerLabelProviderId;

  const { billToAccount } = options || {};
  const hasBillToAccount = !!billToAccount;
  const { accountNumber, billingCountry, billingPostalCode, billToParty } = billToAccount || {};

  const serviceObject = sellerServices.find((service) => service.serviceId === serviceId) || { labelProviderId: '' };
  const selectedSellerLabelProvider =
    sellerLabelProviders
      .filter((slp) => slp.labelProviderId === serviceObject.labelProviderId)
      .find((slp) => slp.sellerLabelProviderId === selectedSellerLabelProviderId) || {};

  return (
    <ReadOnlyOtherShippingOptions
      fulfillmentPlanId={fulfillmentPlanId}
      optionData={optionData}
      sectionsToShow={getSectionsToShow(fulfillmentPlanId, fulfillmentType)}
      shippingAccountCountry={billingCountry}
      shippingAccountName={hasBillToAccount ? getShippingAccountDisplayName(billToParty) : selectedSellerLabelProvider.name || undefined}
      shippingAccountNumber={accountNumber}
      shippingAccountPostalCode={billingPostalCode}
      shouldShowDdp={shouldShowDdp}
    />
  );
});

ReadOnlyOtherShippingOptionsWrapper.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
  optionData: PropTypes.object.isRequired,
};
